/*
 * The Payment Account screen's state, shared by the classic panel and the v3 screen.
 *
 * This is the money screen: the account here is the one that produces every buyer's QR and the one
 * every slip is checked against. Two rules follow, and both live in this file so neither shell can
 * lose one.
 *
 * `identifier` is normalised before it is sent. The merchant providers (K-Shop, แม่มณี, ถุงเงิน)
 * take a REF1 that is upper-case alphanumeric, and a lower-case letter or a stray dash typed into
 * that box is the exact typo class that makes K-Plus refuse the payment at the till.
 *
 * `canAutoVerify` is derived, not stored. The server can only match a masked receiver on a slip
 * when it knows the bank code, and for merchant QR it also needs the BILLERID — so an account
 * without them still takes money and still has to be reviewed by hand, which is a thing the
 * organizer should be told before the queue fills up rather than after.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type { PaymentAccountDto, PaymentAccountInput, PaymentProvider } from '../../api';
import { notifyPaymentAccountsChanged } from './PaymentReceiverWarning';

export const PROVIDER_LABEL: Record<PaymentProvider, string> = {
  promptpay: 'พร้อมเพย์',
  kshop: 'K-Shop',
  mae_manee: 'แม่มณี',
  tungngern: 'ถุงเงิน',
  bank: 'บัญชีธนาคาร',
};

export const PROMPTPAY_TYPE_LABEL: Record<'msisdn' | 'natid' | 'ewallet', string> = {
  msisdn: 'เบอร์โทรศัพท์',
  natid: 'เลขบัตรประชาชน',
  ewallet: 'eWallet ID',
};

/** Keep the payment-account form's immediate validation aligned with the server's write guard. */
export function promptPayIdentifierError(
  identifier: string,
  type: 'msisdn' | 'natid' | 'ewallet' | null | undefined,
): string | null {
  if (!type) return 'เลือกประเภทพร้อมเพย์';
  const invalid = type === 'msisdn'
    ? !/^(?:0\d{9}|66\d{9})$/.test(identifier)
    : type === 'ewallet'
      ? !/^\d{15}$/.test(identifier)
      : !/^\d{13}$/.test(identifier) || (() => {
        const sum = Array.from(identifier.slice(0, 12), (digit, index) => Number(digit) * (13 - index))
          .reduce((total, digit) => total + digit, 0);
        return Number(identifier[12]) !== (11 - (sum % 11)) % 10;
      })();
  if (!invalid) return null;
  return type === 'msisdn'
    ? 'เบอร์พร้อมเพย์ต้องเป็นตัวเลข 10 หลักขึ้นต้น 0 หรือ 66 ตามด้วย 9 หลัก'
    : type === 'natid'
      ? 'เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลักและเลขตรวจสอบถูกต้อง'
      : 'eWallet ID ต้องเป็นตัวเลข 15 หลัก';
}

/** The merchant providers whose identifier is a REF1 rather than a phone number. */
export const REF1_PROVIDERS: PaymentProvider[] = ['kshop', 'mae_manee', 'tungngern'];

export function emptyAccountDraft(provider: PaymentProvider): PaymentAccountInput {
  return {
    provider,
    identifier: '',
    promptpayType: provider === 'promptpay' ? 'msisdn' : null,
    merchantName: null,
    displayName: '',
    bankName: null,
    bankCode: null,
    qrImageKey: null,
    receiverProxyId: null,
  };
}

export function normalizeIdentifier(provider: PaymentProvider, raw: string): string {
  return REF1_PROVIDERS.includes(provider) ? raw.toUpperCase().replace(/[^A-Z0-9]/g, '') : raw.trim();
}

/**
 * Whether a slip paid into this account can be matched automatically.
 *
 * A bank account needs its BOT code; a merchant QR needs the BILLERID as well. Without them the
 * money still arrives — only the matching is manual.
 */
export function canAutoVerify(account: PaymentAccountDto): boolean {
  if (!account.bankCode) return false;
  if (REF1_PROVIDERS.includes(account.provider)) return !!account.receiverProxyId;
  return true;
}

export interface PaymentAccountsState {
  accounts: PaymentAccountDto[];
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  /** The account open in the editor, or `'new'` while adding one. */
  selectedId: string | 'new' | null;
  select: (id: string | 'new' | null) => void;
  draft: PaymentAccountInput;
  setDraft: (next: PaymentAccountInput) => void;
  draftError: string;
  identifierError: string | null;
  saving: boolean;
  qr: { loading: boolean; dataUrl: string; error: string };
  testQr: () => Promise<void>;
  create: () => Promise<void>;
  update: (id: string, patch: Partial<PaymentAccountInput> & { enabled?: boolean }) => Promise<void>;
  remove: (id: string) => Promise<void>;
  makeDefault: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

export function usePaymentAccounts(token: string, eventSlug?: string): PaymentAccountsState {
  const [accounts, setAccounts] = useState<PaymentAccountDto[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<PaymentAccountInput>(emptyAccountDraft('promptpay'));
  const [draftError, setDraftError] = useState('');
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState({ loading: false, dataUrl: '', error: '' });

  const load = useCallback(async () => {
    try {
      setAccounts(await adminApi.listPaymentAccounts(token, eventSlug));
      setLoadError('');
      notifyPaymentAccountsChanged();
    } catch {
      setLoadError('โหลดรายการบัญชีไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (id: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  return {
    accounts: accounts ?? [],
    loading: accounts === null && !loadError,
    loadError, error, busyId, selectedId,
    select: (id) => {
      setSelectedId(id);
      setDraftError('');
      setQr({ loading: false, dataUrl: '', error: '' });
      if (id === 'new') setDraft(emptyAccountDraft('promptpay'));
      else if (id) {
        const found = (accounts ?? []).find((a) => a.id === id);
        if (found) {
          setDraft({
            provider: found.provider,
            identifier: found.identifier,
            promptpayType: found.promptpayType,
            merchantName: found.merchantName,
            displayName: found.displayName,
            bankName: found.bankName,
            bankCode: found.bankCode,
            qrImageKey: found.qrImageKey,
            receiverProxyId: found.receiverProxyId,
          });
        }
      }
    },
    draft,
    setDraft: (next) => {
      setDraft(next);
      setDraftError('');
    },
    draftError,
    identifierError: draft.provider === 'promptpay' && draft.identifier
      ? promptPayIdentifierError(draft.identifier, draft.promptpayType)
      : null,
    saving, qr,
    testQr: async () => {
      const identifier = normalizeIdentifier(draft.provider, draft.identifier);
      if (!identifier || draft.provider === 'bank') return;
      if (draft.provider === 'promptpay') {
        const invalid = promptPayIdentifierError(identifier, draft.promptpayType);
        if (invalid) { setQr({ loading: false, dataUrl: '', error: invalid }); return; }
      }
      setQr({ loading: true, dataUrl: '', error: '' });
      try {
        const { payload } = await adminApi.testPaymentAccountQr(
          token,
          { provider: draft.provider, identifier, promptpayType: draft.promptpayType, merchantName: draft.merchantName },
          eventSlug,
        );
        const QRCode = (await import('qrcode')).default;
        const dataUrl = await QRCode.toDataURL(payload, { margin: 1, width: 200 });
        setQr({ loading: false, dataUrl, error: '' });
      } catch (e) {
        setQr({ loading: false, dataUrl: '', error: e instanceof ApiError ? e.message : 'สร้าง QR ทดสอบไม่สำเร็จ' });
      }
    },
    create: async () => {
      const identifier = normalizeIdentifier(draft.provider, draft.identifier);
      setDraftError('');
      if (!identifier || !draft.displayName.trim()) {
        setDraftError('ต้องมีชื่อเรียกบัญชีและเลขบัญชี/เลขร้านค้า');
        return;
      }
      if (draft.provider === 'promptpay') {
        const invalid = promptPayIdentifierError(identifier, draft.promptpayType);
        if (invalid) { setDraftError(invalid); return; }
      }
      setSaving(true);
      try {
        const created = await adminApi.createPaymentAccount(token, { ...draft, identifier }, eventSlug);
        await load();
        setSelectedId(created.id);
      } catch (e) {
        setDraftError(e instanceof ApiError ? e.message : 'บันทึกบัญชีไม่สำเร็จ');
      } finally {
        setSaving(false);
      }
    },
    update: async (id, patch) => {
      const normalized = patch.identifier !== undefined && patch.provider !== undefined
        ? { ...patch, identifier: normalizeIdentifier(patch.provider, patch.identifier) }
        : patch;
      setSaving(true);
      setDraftError('');
      if (draft.provider === 'promptpay') {
        const invalid = promptPayIdentifierError(draft.identifier, draft.promptpayType);
        if (invalid) { setDraftError(invalid); setSaving(false); return; }
      }
      try {
        await adminApi.updatePaymentAccount(token, id, normalized, eventSlug);
        await load();
      } catch (e) {
        setDraftError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
      } finally {
        setSaving(false);
      }
    },
    remove: async (id) => {
      await act(id, () => adminApi.deletePaymentAccount(token, id, eventSlug), 'ลบไม่สำเร็จ');
      setSelectedId((current) => (current === id ? null : current));
    },
    makeDefault: (id) => act(id, () => adminApi.updatePaymentAccount(token, id, { isDefault: true }, eventSlug), 'ตั้งบัญชีหลักไม่สำเร็จ'),
    reload: load,
  };
}
